import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './store/authStore'
import { useLanguageStore } from './store/languageStore'
import { userAPI } from './api/client'

// Auth & Landing
import LandingPage from './pages/landing/LandingPage'
import AuthPage from './pages/auth/AuthPage'

// Onboarding
import OnboardingPage from './pages/onboarding/OnboardingPage'
import DiagnosticPage from './pages/onboarding/DiagnosticPage'
import RoadmapPage from './pages/onboarding/RoadmapPage'
import AILanguageSelectorSuggestionPage from './pages/onboarding/AILanguageSelectorSuggestionPage'

// Core App
import DashboardPage from './pages/dashboard/DashboardPage'
import SessionPage from './pages/session/SessionPage'
import SessionCompletionRewardsPage from './pages/session/SessionCompletionRewardsPage'
import LibraryPage from './pages/library/LibraryPage'

// Skills
import WritingPage from './pages/skills/WritingPage'
import WritingRewriteComparisonPage from './pages/writing/WritingRewriteComparisonPage'
import SpeakingPage from './pages/skills/SpeakingPage'
import AISpeakingPictureComparisonPage from './pages/speaking/AISpeakingPictureComparisonPage'
import ShadowingPage from './pages/speaking/ShadowingPage'
import AISpeakingPronunciationDeepDivePage from './pages/speaking/AISpeakingPronunciationDeepDivePage'
import ListeningPage from './pages/skills/ListeningPage'
import ReadingPage from './pages/skills/ReadingPage'

// Vocabulary
import VocabPage from './pages/vocab/VocabPage'
import VocabDeckPage from './pages/vocab/VocabDeckPage'

// Other learner pages
import ProgressPage from './pages/progress/ProgressPage'
import MockTestPage from './pages/mocktest/MockTestPage'
import ChallengePage from './pages/community/ChallengePage'

// Utility pages
import PricingPage from './pages/pricing/PricingPage'
import HelpPage from './pages/help/HelpPage'
import ProfilePage from './pages/profile/ProfilePage'

// Admin & Teacher
import AdminPage from './pages/admin/AdminPage'
import TeacherPage from './pages/teacher/TeacherPage'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { token } = useAuthStore()
  return token ? <>{children}</> : <Navigate to="/login" replace />
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { token, role } = useAuthStore()
  if (!token) return <Navigate to="/login" replace />
  if (role !== 'ADMIN') return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { token } = useAuthStore()
  return token ? <Navigate to="/dashboard" replace /> : <>{children}</>
}

function RootRedirect() {
  const { token } = useAuthStore()
  return <Navigate to={token ? '/dashboard' : '/home'} replace />
}

export default function App() {
  const { token } = useAuthStore()
  const { setUiLanguage, setLangSuggestionShown } = useLanguageStore()

  // Sync language preference from DB on login
  useEffect(() => {
    if (!token) return
    userAPI.me().then((user: any) => {
      if (user?.uiLanguage) {
        setUiLanguage(user.uiLanguage)
      }
      if (user?.langSuggestionShown !== undefined) {
        setLangSuggestionShown(user.langSuggestionShown)
      }
    }).catch(() => {})
  }, [token]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Routes>
      {/* Root redirect */}
      <Route path="/" element={<RootRedirect />} />

      {/* Public landing */}
      <Route path="/home" element={<LandingPage />} />

      {/* Auth — combined login/register with tabs */}
      <Route path="/login" element={<PublicOnlyRoute><AuthPage defaultTab="login" /></PublicOnlyRoute>} />
      <Route path="/register" element={<PublicOnlyRoute><AuthPage defaultTab="register" /></PublicOnlyRoute>} />

      {/* Onboarding flow */}
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/diagnostic" element={<ProtectedRoute><DiagnosticPage /></ProtectedRoute>} />
      <Route path="/roadmap" element={<ProtectedRoute><RoadmapPage /></ProtectedRoute>} />
      <Route path="/language-setup" element={<ProtectedRoute><AILanguageSelectorSuggestionPage /></ProtectedRoute>} />

      {/* Core App */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/session" element={<ProtectedRoute><SessionPage /></ProtectedRoute>} />
      <Route path="/session/complete" element={<ProtectedRoute><SessionCompletionRewardsPage /></ProtectedRoute>} />
      <Route path="/library" element={<ProtectedRoute><LibraryPage /></ProtectedRoute>} />

      {/* Skill practice */}
      <Route path="/writing" element={<ProtectedRoute><WritingPage /></ProtectedRoute>} />
      <Route path="/writing/rewrite-comparison" element={<ProtectedRoute><WritingRewriteComparisonPage /></ProtectedRoute>} />
      <Route path="/speaking" element={<ProtectedRoute><SpeakingPage /></ProtectedRoute>} />
      <Route path="/speaking/shadowing" element={<ProtectedRoute><ShadowingPage /></ProtectedRoute>} />
      <Route path="/speaking/picture-comparison" element={<ProtectedRoute><AISpeakingPictureComparisonPage /></ProtectedRoute>} />
      <Route path="/speaking/pronunciation-deep-dive" element={<ProtectedRoute><AISpeakingPronunciationDeepDivePage /></ProtectedRoute>} />
      <Route path="/listening" element={<ProtectedRoute><ListeningPage /></ProtectedRoute>} />
      <Route path="/reading" element={<ProtectedRoute><ReadingPage /></ProtectedRoute>} />

      {/* Vocabulary */}
      <Route path="/vocab" element={<ProtectedRoute><VocabPage /></ProtectedRoute>} />
      <Route path="/vocab/decks" element={<ProtectedRoute><VocabDeckPage /></ProtectedRoute>} />

      {/* Progress & Community */}
      <Route path="/progress" element={<ProtectedRoute><ProgressPage /></ProtectedRoute>} />
      <Route path="/mock-test" element={<ProtectedRoute><MockTestPage /></ProtectedRoute>} />
      <Route path="/challenges" element={<ProtectedRoute><ChallengePage /></ProtectedRoute>} />

      {/* Utility */}
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/settings" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

      {/* Admin & Teacher */}
      <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
      <Route path="/admin/:tab" element={<AdminRoute><AdminPage /></AdminRoute>} />
      <Route path="/teacher" element={<ProtectedRoute><TeacherPage /></ProtectedRoute>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  )
}
